import React from 'react';
import { Box, Button, Container, TextField, Typography } from '@mui/material';
import { getPolyglot } from '../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import { useNavigate } from 'react-router-dom';
import { SubWorkspaceCreateRequest } from '../../../redux/stoerkID/StoerkIdAPI.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';

function OrganizationCreateForm() {
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const myWorkspace = useAppSelector(getMyWorkspaceSelector);
  const currentUserId = myWorkspace?.users?.[0] ?? '';

  const validationSchema = Yup.object({
    workspaceName: Yup.string().required(
      polyglot.t('stoerk_id.organization_create.name_required')
    ),
    userQuota: Yup.number()
      .min(1, polyglot.t('stoerk_id.organization_create.quota_min'))
      .required(
        polyglot.t('stoerk_id.organization_create.user_quota_required')
      ),
    deviceQuota: Yup.number()
      .min(1, polyglot.t('stoerk_id.organization_create.quota_min'))
      .required(
        polyglot.t('stoerk_id.organization_create.device_quota_required')
      ),
  });

  const formik = useFormik({
    initialValues: {
      workspaceName: '',
      userQuota: 10,
      deviceQuota: 100,
      // Billing fields (mocked — will be wired to API once backend confirms)
      billingFirstName: '',
      billingLastName: '',
      billingStreet: '',
      billingZipCode: '',
      billingCity: '',
      billingCountry: '',
      billingEmail: '',
      billingPhone: '',
      billingVat: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!myWorkspace?.id) return;

      const createRequest: SubWorkspaceCreateRequest = {
        workspaceName: values.workspaceName,
        userQuota: values.userQuota,
        deviceQuota: values.deviceQuota,
        creatorId: currentUserId,
      };

      try {
        await dispatch(
          StoerkIdAPI.endpoints.createSubWorkspace.initiate({
            workspaceId: myWorkspace.id,
            formData: createRequest,
          })
        ).unwrap();
        navigate('/account/organizations');
      } catch (error) {
        console.error('Failed to create organization:', error);
      }
    },
  });

  return (
    <Container maxWidth="md" sx={{ padding: 3 }} disableGutters>
      <Typography variant="h1" mb={1}>
        {polyglot.t('stoerk_id.organization_create.title')}
      </Typography>
      <Typography mb={2}>
        {polyglot.t('stoerk_id.organization_create.description')}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        {/* Workspace name */}
        <TextField
          fullWidth
          id="workspaceName"
          name="workspaceName"
          label={polyglot.t('stoerk_id.organization_create.workspace_name')}
          value={formik.values.workspaceName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.workspaceName && Boolean(formik.errors.workspaceName)
          }
          helperText={
            formik.touched.workspaceName && formik.errors.workspaceName
          }
          margin="normal"
          required
        />

        {/* Billing information (mocked — saved locally until API is ready) */}
        <Typography variant="h3" mt={3} mb={1}>
          {polyglot.t('stoerk_id.organization_create.billing_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {polyglot.t('stoerk_id.organization_create.billing_note')}
        </Typography>

        <TextField
          fullWidth
          id="billingFirstName"
          name="billingFirstName"
          label={polyglot.t('stoerk_id.organization_create.billing_first_name')}
          value={formik.values.billingFirstName}
          onChange={formik.handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          id="billingLastName"
          name="billingLastName"
          label={polyglot.t('stoerk_id.organization_create.billing_last_name')}
          value={formik.values.billingLastName}
          onChange={formik.handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          id="billingStreet"
          name="billingStreet"
          label={polyglot.t('stoerk_id.organization_create.billing_street')}
          value={formik.values.billingStreet}
          onChange={formik.handleChange}
          margin="normal"
        />
        <Box display="flex" gap={2}>
          <TextField
            id="billingZipCode"
            name="billingZipCode"
            label={polyglot.t('stoerk_id.organization_create.billing_zip_code')}
            value={formik.values.billingZipCode}
            onChange={formik.handleChange}
            margin="normal"
            sx={{ flex: 1 }}
          />
          <TextField
            id="billingCity"
            name="billingCity"
            label={polyglot.t('stoerk_id.organization_create.billing_city')}
            value={formik.values.billingCity}
            onChange={formik.handleChange}
            margin="normal"
            sx={{ flex: 2 }}
          />
        </Box>
        <TextField
          fullWidth
          id="billingCountry"
          name="billingCountry"
          label={polyglot.t('stoerk_id.organization_create.billing_country')}
          value={formik.values.billingCountry}
          onChange={formik.handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          id="billingEmail"
          name="billingEmail"
          label={polyglot.t('stoerk_id.organization_create.billing_email')}
          value={formik.values.billingEmail}
          onChange={formik.handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          id="billingPhone"
          name="billingPhone"
          label={polyglot.t('stoerk_id.organization_create.billing_phone')}
          value={formik.values.billingPhone}
          onChange={formik.handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          id="billingVat"
          name="billingVat"
          label={polyglot.t('stoerk_id.organization_create.billing_vat')}
          value={formik.values.billingVat}
          onChange={formik.handleChange}
          margin="normal"
        />

        {/* Quotas */}
        <Typography variant="h3" mt={3} mb={1}>
          {polyglot.t('stoerk_id.organization_create.quota_title')}
        </Typography>

        <TextField
          id="userQuota"
          name="userQuota"
          label={polyglot.t('stoerk_id.organization_create.user_quota')}
          type="number"
          value={formik.values.userQuota}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.userQuota && Boolean(formik.errors.userQuota)}
          helperText={formik.touched.userQuota && formik.errors.userQuota}
          margin="normal"
          required
        />
        <TextField
          id="deviceQuota"
          name="deviceQuota"
          label={polyglot.t('stoerk_id.organization_create.device_quota')}
          type="number"
          value={formik.values.deviceQuota}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.deviceQuota && Boolean(formik.errors.deviceQuota)
          }
          helperText={formik.touched.deviceQuota && formik.errors.deviceQuota}
          margin="normal"
          required
        />

        <Box mt={3}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting}
          >
            {polyglot.t('stoerk_id.organization_create.submit')}
          </Button>
        </Box>
      </form>
    </Container>
  );
}

export default OrganizationCreateForm;
